import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Activity, CalendarDays, Check, ClipboardCheck, MessageSquare, Siren } from "lucide-react";
import { CardFrame, fadeUp } from "./IntakeCards";
import { CountUp } from "@/components/clientDemo/intro/IntroStats";

const P = "exchange-sanctuary-card";
const STATUSES = ["Open", "In Progress", "Resolved"];
const STATUS_TONE = ["bg-amber-500/20 text-amber-300", "bg-sky-500/20 text-sky-300", "bg-emerald-500/20 text-emerald-300"];

export function IncidentCard({ run, active }) {
  const [st, setSt] = useState(2);
  useEffect(() => {
    if (!run) return undefined;
    setSt(0);
    const ids = [setTimeout(() => setSt(1), 1700), setTimeout(() => setSt(2), 3000)];
    return () => ids.forEach(clearTimeout);
  }, [run]);
  return (
    <CardFrame prefix={P} id="incidents" n={1} title="Incident Reporting & Alerts" Icon={Siren} active={active}>
      <div key={run} className="space-y-2 text-xs">
        <motion.div {...fadeUp(0.1)} className="grid grid-cols-3 gap-2">
          {[["Property", "House 2"], ["Category", "Curfew"], ["Severity", "Medium"]].map(([k, v]) => (
            <div key={k} className="rounded-md bg-white/[0.03] border border-white/5 px-2 py-1.5"><div className="text-[10px] uppercase tracking-wider text-slate-500">{k}</div><div className="text-white">{v}</div></div>
          ))}
        </motion.div>
        <motion.div {...fadeUp(0.4)} className="rounded-md bg-white/[0.03] border border-white/5 px-2.5 py-1.5 text-slate-200">Marcus T. · returned 40 min after curfew, cooperative</motion.div>
        <motion.div {...fadeUp(0.9)} data-testid="exchange-sanctuary-sms" className="flex items-center gap-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-1.5 text-[#F4D88A]">
          <MessageSquare className="w-3.5 h-3.5 shrink-0" /> SMS sent · SA · CAO · Facility Ops Director
        </motion.div>
        <div className="flex items-center gap-2 pt-1">
          <span className="text-slate-500">Status</span>
          <motion.span key={st} initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} data-testid="exchange-sanctuary-status"
            className={`px-2 py-0.5 rounded-full font-mono text-[10px] tracking-[0.12em] uppercase ${STATUS_TONE[st]}`}>{STATUSES[st]}</motion.span>
          <span className="ml-auto text-slate-500">Note filed to resident</span>
        </div>
      </div>
    </CardFrame>
  );
}

const PILLARS = ["Case Mgmt", "Life Skills", "Res Check-Up", "Spiritual"];
const ROSTER = [["Marcus T.", 4], ["Dana R.", 3], ["Luis M.", 2]];

export function WorkReadyCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="workready" n={2} title="Work-Ready Board" Icon={ClipboardCheck} active={active}>
      <div key={run} className="space-y-2">
        {ROSTER.map(([name, done], r) => (
          <div key={name} className="flex items-center gap-2 text-xs">
            <span className="w-20 shrink-0 text-white">{name}</span>
            <div className="flex flex-wrap gap-1 flex-1">
              {PILLARS.map((p, i) => (
                <motion.span key={p} initial={{ backgroundColor: "rgba(255,255,255,0.04)" }}
                  animate={{ backgroundColor: i < done ? "rgba(16,185,129,0.18)" : "rgba(255,255,255,0.04)" }}
                  transition={{ delay: 0.3 + r * 0.3 + i * 0.25 }}
                  className={`px-1.5 py-0.5 rounded font-mono text-[9px] ${i < done ? "text-emerald-300" : "text-slate-500"}`}>{p}</motion.span>
              ))}
            </div>
            {done === 4 && (
              <motion.span {...fadeUp(1.8)} data-testid="exchange-sanctuary-workready"
                className="shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-mono text-[9px] tracking-[0.12em] uppercase">
                <Check className="w-3 h-3" /> Work-Ready
              </motion.span>
            )}
          </div>
        ))}
        <div className="pt-1 font-mono text-[10px] text-slate-500">Next-week schedule due Sat 12:00 PM EST</div>
      </div>
    </CardFrame>
  );
}

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const EVENTS = [[0, 0, "Shift · Warehouse"], [1, 1, "Case session"], [2, 0, "Shift · Warehouse"], [3, 2, "Blocked · Staff"], [4, 0, "Res Check-Up"]];

export function CalendarCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="calendar" n={3} title="Campus Calendar & Appointments" Icon={CalendarDays} active={active}>
      <div key={run} className="grid grid-cols-5 gap-1.5 text-[9px] sm:text-[10px]">
        {DAYS.map((d, di) => (
          <div key={d} className="space-y-1">
            <div className="text-center font-mono text-slate-500">{d}</div>
            {[0, 1, 2].map((slot) => {
              const ev = EVENTS.find(([x, y]) => x === di && y === slot);
              const booking = di === 2 && slot === 2;
              return (
                <div key={slot} className="h-11 rounded-md bg-white/[0.03] border border-white/5 relative overflow-hidden">
                  {ev && <motion.div {...fadeUp(0.2 + di * 0.12)} className="absolute inset-0.5 rounded bg-sky-500/15 text-sky-200 px-1 leading-tight flex items-center">{ev[2]}</motion.div>}
                  {booking && (
                    <motion.div data-testid="exchange-sanctuary-booking" initial={{ y: -30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1.4, type: "spring", stiffness: 260, damping: 18 }}
                      className="absolute inset-0.5 rounded bg-[#D4AF37]/30 border border-[#D4AF37]/60 text-[#FFF4CC] px-1 leading-tight flex items-center">Marcus · Interview</motion.div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </CardFrame>
  );
}

const PULSE = [["Active residents", 47], ["Beds occupied", 143, " / 159"], ["Open incidents", 3], ["Staff memos", 12]];

export function CampusPulseCard({ run, active }) {
  return (
    <CardFrame prefix={P} id="pulse" n={4} title="Campus Pulse" Icon={Activity} active={active}>
      <div key={run} className="grid grid-cols-2 gap-2">
        {PULSE.map(([label, to, suffix = ""], i) => (
          <motion.div key={label} {...fadeUp(i * 0.15)} data-testid={`exchange-sanctuary-pulse-${i}`} className="rounded-lg bg-white/[0.03] border border-white/5 px-3 py-2">
            <div className="font-mono text-lg font-bold text-[#F4D88A] tabular-nums"><CountUp to={to} duration={1300} />{suffix}</div>
            <div className="text-[10px] text-slate-400">{label}</div>
          </motion.div>
        ))}
      </div>
      <div className="mt-3 flex items-end gap-1 h-8" aria-hidden>
        {[7, 6, 6, 5, 4, 4, 3, 3].map((h, i) => (
          <motion.div key={`${run}-${i}`} className="flex-1 rounded-sm bg-gradient-to-t from-[#C9A227]/40 to-[#F4D88A]"
            initial={{ height: 0 }} animate={{ height: `${h * 12}%` }} transition={{ delay: 0.4 + i * 0.06 }} />
        ))}
      </div>
      <div className="mt-1 font-mono text-[9px] text-slate-500">Incidents · last 60 days, trending down</div>
    </CardFrame>
  );
}
